import React, { useState, useEffect } from "react";
import {
    Lock, Trash2, Loader2, CheckCircle2, AlertCircle,
    Eye, EyeOff, ShieldAlert, Chrome
} from "lucide-react";
import { Sidebar, Header, ToastContainer } from "../Dashboard/DashboardComponents";
import { authService } from "../../services/auth.service";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { motion, AnimatePresence } from "framer-motion";

// Small re-usable toast hook
const useToast = () => {
    const [toast, setToast] = useState<{ type: "success" | "error"; msg: string } | null>(null);
    const show = (type: "success" | "error", msg: string) => {
        setToast({ type, msg });
        setTimeout(() => setToast(null), 3500);
    };
    return { toast, show };
};

// Delete account confirmation modal
const DeleteModal = ({ provider, onConfirm, onCancel }: {
    provider: string, onConfirm: (pwd?: string) => void, onCancel: () => void
}) => {
    const [pwd, setPwd] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                className="bg-white border border-slate-200 rounded-3xl w-full max-w-md shadow-2xl overflow-hidden"
            >
                <div className="p-10 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-rose-50 flex items-center justify-center mx-auto mb-6">
                        <Trash2 size={28} className="text-rose-600" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Delete Your Account?</h3>
                    <p className="text-slate-500 font-medium text-sm leading-relaxed mb-8">All your projects, uploaded assets, and account data will be permanently deleted. This action cannot be undone.</p>

                    {provider !== "google" && (
                        <div className="relative mb-6 text-left">
                            <label className="text-xs font-bold text-slate-700 ml-1 mb-2 block">Confirm with Password</label>
                            <div className="relative">
                                <Input
                                    type={showPwd ? "text" : "password"}
                                    placeholder="Enter your password"
                                    value={pwd}
                                    onChange={e => setPwd(e.target.value)}
                                    className="h-12 bg-white border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:ring-rose-500/10 focus:border-rose-500 text-sm font-bold"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPwd(!showPwd)}
                                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                                >
                                    {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="flex gap-3">
                        <Button variant="ghost" onClick={onCancel} className="flex-1 h-11 rounded-xl text-slate-500 hover:text-slate-900 font-bold">
                            Cancel
                        </Button>
                        <Button
                            onClick={() => onConfirm(provider !== "google" ? pwd : undefined)}
                            disabled={provider !== "google" && !pwd}
                            className="flex-1 h-11 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-bold transition-all shadow-sm disabled:opacity-50"
                        >
                            Delete Account
                        </Button>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

const Settings = () => {
    const [user, setUser] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [isMobile, setIsMobile] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const { toast, show } = useToast();
    const navigate = useNavigate();

    // Password form
    const [currentPwd, setCurrentPwd] = useState("");
    const [newPwd, setNewPwd] = useState("");
    const [confirmPwd, setConfirmPwd] = useState("");
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [changingPwd, setChangingPwd] = useState(false);
    const [deletingAccount, setDeletingAccount] = useState(false);

    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth < 1024);
            setSidebarOpen(window.innerWidth >= 1024);
        };
        checkScreenSize();
        window.addEventListener("resize", checkScreenSize);

        authService.getProfile()
            .then(setUser)
            .catch(() => navigate("/login"))
            .finally(() => setLoading(false));

        return () => window.removeEventListener("resize", checkScreenSize);
    }, [navigate]);

    const handleChangePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (newPwd !== confirmPwd) { show("error", "New passwords do not match"); return; }

        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
        if (!passwordRegex.test(newPwd)) {
            show("error", "Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character.");
            return;
        }

        setChangingPwd(true);
        try {
            await authService.changePassword({ currentPassword: currentPwd, newPassword: newPwd });
            show("success", "Password changed successfully!");
            setCurrentPwd(""); setNewPwd(""); setConfirmPwd("");
        } catch (err: any) {
            show("error", err.message || "Failed to change password");
        } finally {
            setChangingPwd(false);
        }
    };

    const handleDeleteAccount = async (pwd?: string) => {
        setDeletingAccount(true);
        try {
            await authService.deleteAccount(pwd);
            authService.logout();
            navigate("/login");
        } catch (err: any) {
            show("error", err.message || "Failed to delete account");
            setDeletingAccount(false);
            setShowDeleteModal(false);
        }
    };

    if (loading) {
        return (
            <div className="flex h-screen items-center justify-center bg-slate-50">
                <Loader2 className="h-10 w-10 animate-spin text-indigo-600" />
            </div>
        );
    }

    return (
        <div className="flex h-screen bg-slate-50 text-slate-900 font-sans">
            <Sidebar isOpen={sidebarOpen} isMobile={isMobile} user={user} onToggle={() => setSidebarOpen(!sidebarOpen)} activePage="settings" />
            <div className="flex-1 flex flex-col overflow-hidden relative z-10">
                <Header
                    isMobile={isMobile}
                    onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
                    user={user}
                    onLogout={() => { authService.logout(); navigate("/login"); }}
                    title="Security Settings"
                />
                <ToastContainer />
                <main className="flex-1 overflow-y-auto p-6 lg:p-10 custom-scrollbar">
                    <div className="max-w-3xl mx-auto space-y-10">

                        <div className="mb-4">
                            <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
                                Account Security
                            </h2>
                            <p className="text-slate-500 text-sm mt-1">Configure your password and privacy preferences.</p>
                        </div>

                        {/* Change Password */}
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-white border border-slate-200 rounded-3xl p-8 lg:p-10 shadow-sm"
                        >
                            <div className="flex items-center gap-4 mb-8">
                                <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
                                    <Lock size={20} />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-slate-900">Password</h3>
                                    <p className="text-xs text-slate-500 font-medium">Manage how you access your account.</p>
                                </div>
                            </div>

                            {user?.provider === "google" ? (
                                <div className="flex items-start gap-4 p-5 rounded-2xl bg-indigo-50 border border-indigo-100">
                                    <Chrome size={18} className="text-indigo-600 mt-0.5 shrink-0" />
                                    <p className="text-sm text-indigo-700/80 leading-relaxed font-medium">
                                        Your account is authenticated using your <strong className="text-indigo-900">Google Account</strong>. Security settings can be managed directly in your Google profile.
                                    </p>
                                </div>
                            ) : (
                                <form onSubmit={handleChangePassword} className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-2 md:col-span-2">
                                            <label className="text-xs font-bold text-slate-700 ml-1">Current Password</label>
                                            <div className="relative">
                                                <Input
                                                    type={showCurrent ? "text" : "password"}
                                                    value={currentPwd}
                                                    onChange={e => setCurrentPwd(e.target.value)}
                                                    required
                                                    autoComplete="current-password"
                                                    className="h-12 bg-white border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:ring-indigo-500/10 focus:border-indigo-500 text-sm font-bold"
                                                />
                                                <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                                                    {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
                                                </button>
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-slate-700 ml-1">New Password</label>
                                            <div className="relative">
                                                <Input
                                                    type={showNew ? "text" : "password"}
                                                    value={newPwd}
                                                    onChange={e => setNewPwd(e.target.value)}
                                                    required
                                                    minLength={8}
                                                    autoComplete="new-password"
                                                    className="h-12 bg-white border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:ring-indigo-500/10 focus:border-indigo-500 text-sm font-bold"
                                                />
                                                <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                                                    {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
                                                </button>
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-slate-700 ml-1">Confirm New Password</label>
                                            <div className="relative">
                                                <Input
                                                    type="password"
                                                    value={confirmPwd}
                                                    onChange={e => setConfirmPwd(e.target.value)}
                                                    required
                                                    autoComplete="new-password"
                                                    className={`h-12 bg-white border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:ring-indigo-500/10 focus:border-indigo-500 text-sm font-bold ${confirmPwd && confirmPwd !== newPwd ? "border-rose-300 ring-rose-50" : ""}`}
                                                />
                                                {confirmPwd && confirmPwd !== newPwd && (
                                                    <p className="text-[10px] text-rose-600 font-bold uppercase mt-2 ml-1">Passwords do not match</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="pt-2">
                                        <Button
                                            type="submit"
                                            disabled={changingPwd || !currentPwd || !newPwd || newPwd !== confirmPwd}
                                            className="w-full sm:w-auto px-8 h-11 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold font-sans transition-all shadow-md shadow-indigo-100 disabled:opacity-50"
                                        >
                                            {changingPwd ? <Loader2 size={18} className="animate-spin" /> : "Update Password"}
                                        </Button>
                                    </div>
                                </form>
                            )}
                        </motion.div>

                        {/* Danger Zone */}
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="bg-rose-50 border border-rose-100 rounded-3xl p-8 lg:p-10"
                        >
                            <div className="flex items-center gap-4 mb-8">
                                <div className="w-10 h-10 rounded-xl bg-white border border-rose-100 flex items-center justify-center text-rose-600 shadow-sm">
                                    <ShieldAlert size={20} />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-rose-900">Danger Zone</h3>
                                    <p className="text-xs text-rose-700/60 font-medium">Sensitive actions that cannot be reversed.</p>
                                </div>
                            </div>

                            <div className="flex flex-col md:flex-row items-center justify-between gap-6 p-6 rounded-2xl bg-white border border-rose-100/50">
                                <div className="flex-1 text-center md:text-left">
                                    <h4 className="text-base font-bold text-slate-900 mb-1">Account Deletion</h4>
                                    <p className="text-xs text-slate-500 font-medium leading-relaxed">Permanently remove your account and all associated data from VidLaya.</p>
                                </div>
                                <Button
                                    onClick={() => setShowDeleteModal(true)}
                                    disabled={deletingAccount}
                                    variant="outline"
                                    className="h-11 border-rose-200 text-rose-600 hover:bg-rose-600 hover:text-white rounded-xl px-8 font-bold transition-all shrink-0"
                                >
                                    Delete My Account
                                </Button>
                            </div>
                        </motion.div>

                    </div>
                </main>
            </div>

            {showDeleteModal && (
                <DeleteModal
                    provider={user?.provider || "local"}
                    onConfirm={handleDeleteAccount}
                    onCancel={() => setShowDeleteModal(false)}
                />
            )}

            <AnimatePresence>
                {toast && (
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className={`fixed bottom-8 right-8 z-[100] flex items-center gap-4 px-6 py-4 rounded-2xl shadow-xl border ${toast.type === "success" ? "bg-emerald-50 border-emerald-100 text-emerald-700" : "bg-rose-50 border-rose-100 text-rose-700"}`}
                    >
                        {toast.type === "success" ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
                        <span className="text-sm font-bold">{toast.msg}</span>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Settings;
